import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import cs from 'classnames';

import { useNewRoleContext } from 'lib/context/Role/NewRoleContext/NewRoleContext';
import { ia } from 'lib/helpers/utility';
import { useRoleUsers } from 'lib/hooks/queries/permissions/usePracticeMembers';
import useDebounce from 'lib/hooks/useDebounce';

import ErrorBoundary from 'components/shared/Error/Boundary';
import ErrorMessage from 'components/shared/ErrorMessage/ErrorMessage';
import Icon from 'components/shared/Icon/Icon';
import Input from 'components/shared/Input/Input';
import Skeleton from 'components/shared/Skeleton/Skeleton';

import AssignUser from './components/AssignUsers';

const AssignRole = ({ forAdmin }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { newRoleSelectedUsers, setNewRoleSelectedUsers } = useNewRoleContext();
  const { id } = useParams();
  const { data = {}, isLoading } = useRoleUsers({
    params: { searchTerm: debouncedSearchTerm, practice_id: id, forAdmin },
    dependencies: [debouncedSearchTerm, id, forAdmin]
  });

  const members = data?.users;

  useEffect(() => {
    return () => {
      setNewRoleSelectedUsers(0);
    };
  }, []);

  return (
    <ErrorBoundary FallbackComponent={ErrorMessage}>
      {isLoading ? (
        <Skeleton count={1} height={1020} />
      ) : (
        <div className="inline-flex h-fit w-full shrink grow basis-0 flex-col items-start justify-start gap-[21px] self-stretch px-9 pt-10">
          <div className="inline-flex h-[42px] items-center justify-between self-stretch">
            <div className="text-center text-base font-medium leading-normal text-primary-900">
              Assign newly created role to staff members.
            </div>
            <div className="flex h-[42px] items-center justify-start gap-4">
              <div
                className={cs(
                  ' pt-3 text-center text-sm font-medium leading-snug text-primary-900',
                  newRoleSelectedUsers === 0 && 'opacity-25'
                )}>
                <span>{newRoleSelectedUsers} selected</span>
              </div>
              <div className="flex !h-[42px] w-[200px] items-center justify-start gap-2.5 !outline-none focus-within:!border-none">
                <Input
                  id="searchStaff"
                  type="string"
                  placeholder="Search staff"
                  value={searchTerm}
                  icon="search-linear"
                  inputWrapperClassName="w-[200px] h-[42px] !rounded-none !bg-transparent !py-2 !pb-1 !pl-0 border-b-[1px] !border-neutral-200 border-0 justify-start items-center gap-3 inline-flex !outline-transparent focus-within:!border-b-[1px] focus-within:!border-neutral-200 focus-within:!outline-transparent"
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  rightText={
                    searchTerm && (
                      <Icon
                        icon="close-linear"
                        onClick={() => {
                          setSearchTerm('');
                        }}
                      />
                    )
                  }
                />
              </div>
            </div>
          </div>
          {ia(members) && members.map((member) => <AssignUser member={member} key={member.id} />)}
        </div>
      )}
    </ErrorBoundary>
  );
};

export default AssignRole;
